import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import { ApiFactory } from '../api/easypos-api';
import { ISale, IRefund, RefundMethod } from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { getOrCreateDeviceId } from '../datatypes/device';
import { newClientUlid } from '../datatypes/ulid';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// D2 — sales history with a partial-capable Refund per completed sale.
// Lists today's sales; the operator drills into a completed sale, sees
// existing refunds for it, ticks the problem items (per quantity), and
// sets the amount to return (defaults to the picked items' subtotal,
// adjustable up to the still-refundable total as apology money). Active
// orders are NOT refunded here — they are cancelled from the active board.
@customElement('sales-history-page')
export class SalesHistoryPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _sales: ISale[] = [];
  // saleId -> [refunds]. Populated lazily per sale.
  @state() private _refundsBySale = new Map<string, IRefund[]>();

  @state() private _refundDialogOpen = false;
  @state() private _refundSale: ISale | null = null;
  @state() private _refundMethod: RefundMethod = 'cash';
  @state() private _refundReason = '';
  // lineIndex -> units selected for refund (0 / absent = not selected).
  @state() private _refundQty = new Map<number, number>();
  // Money to return. Auto-tracks the selected subtotal until the operator
  // edits it (then _refundAmountTouched stays true and we stop syncing).
  @state() private _refundAmount = 0;
  @state() private _refundAmountTouched = false;
  // Stable per-dialog-session key so a network retry of the same refund
  // (or a re-tap after a lost response) converges instead of duplicating.
  private _refundIdempotencyKey = '';
  @state() private _busy = false;

  // The UTC trading date we're viewing (yyyymmdd) — sales are stored in
  // per-UTC-day partitions, so the day picker maps 1:1 to a fetch. Defaults to
  // today (UTC). Date nav re-fetches; filters below are applied client-side
  // within the loaded day.
  @state() private _selectedDate = SalesHistoryPage._todayYyyyMmDd();
  // Free-text filter — matches receipt # (with or without a leading #), the
  // member name, or any line-item name.
  @state() private _search = '';
  // Time-of-day block within the day; '' = all.
  @state() private _timeBlock = '';
  // Payment-method filter; '' = all.
  @state() private _payFilter = '';

  // The sale opened in the read-only detail dialog (tap a row).
  @state() private _detailSale: ISale | null = null;
  @state() private _detailOpen = false;

  private static _todayYyyyMmDd(): string {
    const d = new Date();
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(
      d.getUTCDate()
    ).padStart(2, '0')}`;
  }

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .sale-card {
        margin-bottom: 10px;
        padding: 14px 16px;
        background: var(--s-surface, #fff);
        border: 1px solid var(--s-line, rgba(15, 30, 55, 0.08));
        border-radius: 16px;
        box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
      }
      .sale-card.is-refunded {
        opacity: 0.78;
      }
      .sale-row {
        display: flex;
        align-items: flex-start;
        gap: 14px;
      }
      .sale-row .body {
        flex: 1 1 auto;
        min-width: 0;
      }
      .meta {
        font-size: 0.85rem;
        color: var(--s-ink-soft, #666);
        display: flex;
        gap: 6px 12px;
        align-items: center;
        flex-wrap: wrap;
      }
      .meta .seq {
        font-family: var(--font-display, inherit);
        font-weight: 700;
        font-size: 1rem;
        color: var(--s-ink, currentColor);
      }
      .total {
        font-weight: 700;
        font-size: 1.15rem;
        font-family: var(--font-display, inherit);
        font-variant-numeric: tabular-nums;
        color: var(--s-ink, currentColor);
        text-align: right;
      }
      .sale-lines {
        margin-top: 8px;
        font-size: 0.9rem;
        color: var(--s-ink-soft, #666);
        line-height: 1.4;
      }
      .refunds-row {
        margin-top: 10px;
        font-size: 0.85rem;
      }
      .refunds-row .badge-row {
        display: flex;
        gap: 6px;
        align-items: center;
        flex-wrap: wrap;
      }
      .trail {
        flex: none;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
      }
      .empty {
        padding: 32px 24px;
        background: var(--s-canvas-soft, #f7f7f7);
        border-radius: 16px;
        color: var(--s-ink-soft, #666);
        text-align: center;
      }
      .refund-lines {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin: 10px 0 16px;
      }
      .refund-line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 8px 10px;
        border: 1px solid var(--s-line, rgba(15, 30, 55, 0.08));
        border-radius: 12px;
        background: var(--s-surface, #fff);
      }
      .refund-line.is-picked {
        border-color: var(--sl-color-warning-400, #f59e0b);
        background: var(--sl-color-warning-50, #fffbeb);
      }
      .refund-line.is-done {
        opacity: 0.6;
      }
      .rl-name {
        display: flex;
        flex-direction: column;
        min-width: 0;
        font-weight: 600;
      }
      .rl-name small {
        font-weight: 400;
        color: var(--s-ink-soft, #666);
      }
      .rl-note {
        font-size: 0.85rem;
        color: var(--s-ink-soft, #666);
      }
      .rl-stepper {
        flex: none;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 1.3rem;
      }
      .rl-qty {
        min-width: 1.4em;
        text-align: center;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        font-size: 1rem;
      }
      .refund-amount {
        margin-bottom: 16px;
      }
      .amount-hint {
        display: block;
        margin-top: 4px;
        color: var(--s-ink-soft, #666);
      }

      /* --- Toolbar: date nav + filters --- */
      .toolbar {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 14px;
      }
      .date-nav {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .date-input {
        font: inherit;
        padding: 5px 8px;
        border: 1px solid var(--s-line, rgba(15, 30, 55, 0.15));
        border-radius: 10px;
        background: var(--s-surface, #fff);
        color: inherit;
      }
      .filters {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .filters .search {
        flex: 1 1 220px;
        min-width: 160px;
      }
      .filters sl-select {
        flex: 0 1 160px;
      }
      .result-count {
        font-size: 0.82rem;
        color: var(--s-ink-soft, #666);
        margin-bottom: 8px;
      }
      .sale-card.tappable {
        cursor: pointer;
      }
      .sale-card.tappable:hover {
        border-color: var(--sl-color-primary-300, #93c5fd);
      }
      .sale-card.tappable:focus-visible {
        outline: 2px solid var(--sl-color-primary-500, #3b82f6);
        outline-offset: 2px;
      }

      /* --- Detail dialog --- */
      .detail-head {
        margin-bottom: 14px;
      }
      .detail-member {
        margin-top: 8px;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.95rem;
      }
      .detail-member.muted {
        color: var(--s-ink-soft, #666);
      }
      table.detail-lines {
        width: 100%;
        border-collapse: collapse;
        font-variant-numeric: tabular-nums;
        margin-bottom: 14px;
      }
      table.detail-lines td {
        padding: 7px 4px;
        border-bottom: 1px solid var(--s-line, #eee);
        vertical-align: top;
      }
      .dl-qty {
        width: 2.5em;
        font-weight: 700;
        color: var(--s-ink-soft, #666);
      }
      .dl-name {
        width: auto;
      }
      .dl-variant {
        color: var(--s-ink-soft, #666);
      }
      .dl-opts {
        display: inline-block;
        margin-left: 6px;
        font-size: 0.78rem;
        color: var(--s-ink-soft, #666);
      }
      .dl-mods {
        margin-top: 2px;
        font-size: 0.82rem;
        color: var(--s-ink-soft, #666);
      }
      .dl-amt {
        text-align: right;
        white-space: nowrap;
        font-weight: 600;
      }
      .detail-totals {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-variant-numeric: tabular-nums;
        max-width: 280px;
        margin-left: auto;
      }
      .detail-totals > div {
        display: flex;
        justify-content: space-between;
        gap: 24px;
      }
      .detail-totals .grand {
        font-weight: 700;
        font-size: 1.05rem;
        border-top: 1px solid var(--s-line, #eee);
        padding-top: 4px;
        margin-top: 2px;
      }
      .detail-totals .refunded {
        color: var(--s-danger, rgb(160, 25, 25));
      }
      .detail-refunds {
        margin-top: 18px;
      }
      .detail-refunds h4 {
        margin: 0 0 8px;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--s-ink-soft, #666);
      }
      .rfd-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        font-variant-numeric: tabular-nums;
      }
      .rfd-amt {
        font-weight: 600;
      }
      .muted {
        color: var(--s-ink-soft, #666);
      }
    `,
  ];

  // Tracks the shop we've loaded for so updated() can (re)load when the
  // activeShop context first arrives or changes — see updated().
  private _loadedShopName: string | null = null;

  // On a hard navigation / page refresh this component can mount before
  // the activeShop context has propagated; loading from connectedCallback
  // alone would early-return (no shop) and leave the page stuck on
  // "Loading...". Trigger the load once the shop is available, and re-run
  // it if the active shop changes. Keyed on shop name so it never loops.
  override updated(changed: Map<string | number | symbol, unknown>): void {
    super.updated(changed);
    const shop = this.activeShop?.shopName ?? null;
    if (shop && shop !== this._loadedShopName) {
      this._loadedShopName = shop;
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (!this.activeShop) return;
    const shopName = this.activeShop.shopName;
    this._loading = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.listSales(shopName, this._selectedDate);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not load sales.';
        return;
      }
      this._sales = r.data;
      // Eager-load refunds for each sale so the badge state shows.
      const refundsMap = new Map<string, IRefund[]>();
      await Promise.all(
        this._sales.map(async (s) => {
          const date = this._yyyymmdd(s.createdUtc);
          const rr = await api.listRefundsForSale(shopName, s.id, date);
          if (rr.success && rr.data) refundsMap.set(s.id, rr.data);
        })
      );
      this._refundsBySale = refundsMap;
    } finally {
      this._loading = false;
    }
  }

  private _yyyymmdd(iso: string): string {
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}${m}${day}`;
  }

  // --- Date navigation (per-UTC-day, matching the storage partition) ---

  private _dateFromYyyyMmDd(s: string): Date {
    return new Date(
      Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8))
    );
  }

  private _isToday(): boolean {
    return this._selectedDate === SalesHistoryPage._todayYyyyMmDd();
  }

  private _dateLabel(): string {
    return this._dateFromYyyyMmDd(this._selectedDate).toLocaleDateString(
      undefined,
      {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }
    );
  }

  private _goToDate(next: string): void {
    if (next > SalesHistoryPage._todayYyyyMmDd()) return; // never the future
    if (next === this._selectedDate) return;
    this._selectedDate = next;
    // Filters are day-local; reset on day change so a stale filter doesn't hide
    // the new day's rows.
    this._search = '';
    this._timeBlock = '';
    this._payFilter = '';
    void this._load();
  }

  private _shiftDay(days: number): void {
    const d = this._dateFromYyyyMmDd(this._selectedDate);
    d.setUTCDate(d.getUTCDate() + days);
    this._goToDate(
      `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(
        d.getUTCDate()
      ).padStart(2, '0')}`
    );
  }

  private _onDatePick(e: Event): void {
    const v = (e.target as HTMLInputElement).value; // yyyy-mm-dd
    if (v) this._goToDate(v.replace(/-/g, ''));
  }

  // --- Filtering (client-side, within the loaded day) ---

  // Operator-local time-of-day block (browser local = on-site shop time).
  private _timeBlockOf(sale: ISale): string {
    const h = new Date(sale.createdUtc).getHours();
    if (h >= 5 && h < 11) return 'morning';
    if (h >= 11 && h < 14) return 'midday';
    if (h >= 14 && h < 17) return 'afternoon';
    if (h >= 17 && h < 22) return 'evening';
    return 'late';
  }

  private _filteredSales(): ISale[] {
    const q = this._search.trim().toLowerCase().replace(/^#/, '');
    return this._sales.filter((s) => {
      if (
        this._payFilter &&
        !s.tenders.some((t) => t.method === this._payFilter)
      )
        return false;
      if (this._timeBlock && this._timeBlockOf(s) !== this._timeBlock)
        return false;
      if (q) {
        const hay = [
          s.daySequence ? String(s.daySequence) : '',
          s.memberDisplayName ?? '',
          // N.7.4 — so "show me Ana's sales" is a search rather than a report.
          s.actingStaffDisplayName ?? '',
          ...s.lines.map((l) => l.nameSnapshot),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  private _hasActiveFilters(): boolean {
    return !!(this._search.trim() || this._timeBlock || this._payFilter);
  }

  // --- Detail dialog ---

  private _openDetail(sale: ISale): void {
    this._detailSale = sale;
    this._detailOpen = true;
  }

  // Refund from inside the detail view: close detail first, then open the
  // refund dialog (sequential, never two stacked full-page dialogs on phone).
  private _refundFromDetail(sale: ISale): void {
    this._detailOpen = false;
    this._openRefundDialog(sale);
  }

  private _refundsFor(sale: ISale): IRefund[] {
    return this._refundsBySale.get(sale.id) ?? [];
  }

  private _completedRefunds(sale: ISale): IRefund[] {
    return this._refundsFor(sale).filter((r) => r.status === 'completed');
  }

  // Units of a sale line already returned by prior completed refunds.
  private _priorRefundedQty(sale: ISale, lineIndex: number): number {
    return this._completedRefunds(sale)
      .flatMap((r) => r.lines)
      .filter((l) => l.sourceLineIndex === lineIndex)
      .reduce((sum, l) => sum + l.quantity, 0);
  }

  // Units of a sale line still refundable.
  private _remainingQty(sale: ISale, lineIndex: number): number {
    return Math.max(
      0,
      sale.lines[lineIndex].quantity - this._priorRefundedQty(sale, lineIndex)
    );
  }

  // Money still refundable on the sale (rounded to cents to dodge fp dust).
  private _remainingMoney(sale: ISale): number {
    const refunded = this._completedRefunds(sale).reduce(
      (sum, r) => sum + r.amount,
      0
    );
    return Math.round((sale.total - refunded) * 100) / 100;
  }

  private _totalRemainingQty(sale: ISale): number {
    return sale.lines.reduce(
      (sum, _l, i) => sum + this._remainingQty(sale, i),
      0
    );
  }

  // A sale is refundable when it's been delivered and there's still both
  // money and units left to give back.
  private _canRefund(sale: ISale): boolean {
    return (
      sale.status === 'completed' &&
      this._remainingMoney(sale) > 0 &&
      this._totalRemainingQty(sale) > 0
    );
  }

  private _refundState(sale: ISale): 'none' | 'partial' | 'full' {
    if (this._completedRefunds(sale).length === 0) return 'none';
    return this._remainingMoney(sale) <= 0 ||
      this._totalRemainingQty(sale) === 0
      ? 'full'
      : 'partial';
  }

  // Subtotal of the currently-selected problem units.
  private _selectedSubtotal(sale: ISale): number {
    let sum = 0;
    for (const [idx, qty] of this._refundQty) {
      if (qty > 0) sum += sale.lines[idx].priceSnapshot * qty;
    }
    return Math.round(sum * 100) / 100;
  }

  private _selectedUnits(): number {
    let n = 0;
    for (const qty of this._refundQty.values()) n += qty;
    return n;
  }

  private _openRefundDialog(sale: ISale): void {
    this._refundSale = sale;
    this._refundMethod = (
      sale.tenders.some((t) => t.method === 'card') ? 'card' : 'cash'
    ) as RefundMethod;
    this._refundReason = '';
    this._refundQty = new Map();
    this._refundAmount = 0;
    this._refundAmountTouched = false;
    this._refundIdempotencyKey = newClientUlid();
    this._refundDialogOpen = true;
  }

  private _setLineQty(sale: ISale, lineIndex: number, qty: number): void {
    const clamped = Math.max(
      0,
      Math.min(qty, this._remainingQty(sale, lineIndex))
    );
    const next = new Map(this._refundQty);
    if (clamped === 0) next.delete(lineIndex);
    else next.set(lineIndex, clamped);
    this._refundQty = next;
    // Keep the amount in step with the picked items until the operator
    // overrides it, capped at what's still refundable.
    if (!this._refundAmountTouched) {
      this._refundAmount = Math.min(
        this._selectedSubtotal(sale),
        this._remainingMoney(sale)
      );
    }
  }

  private _refundValid(sale: ISale): boolean {
    return (
      this._selectedUnits() > 0 &&
      this._refundAmount > 0 &&
      this._refundAmount <= this._remainingMoney(sale)
    );
  }

  private async _submitRefund(): Promise<void> {
    if (this._busy || !this.activeShop || !this._refundSale) return;
    const sale = this._refundSale;
    if (!this._refundValid(sale)) return;
    this._busy = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      const deviceId = await getOrCreateDeviceId();
      const lines = [...this._refundQty.entries()]
        .filter(([, qty]) => qty > 0)
        .map(([lineIndex, quantity]) => ({ lineIndex, quantity }));
      const r = await api.createRefund(this.activeShop.shopName, {
        saleId: sale.id,
        saleDate: this._yyyymmdd(sale.createdUtc),
        refundMethod: this._refundMethod,
        lines,
        amount: this._refundAmount,
        reason: this._refundReason.trim() || undefined,
        deviceId,
        idempotencyKey: this._refundIdempotencyKey,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not process refund.';
        return;
      }
      this._refundDialogOpen = false;
      this._refundSale = null;
      await this._load();
    } finally {
      this._busy = false;
    }
  }

  private _payIcon(method: string): string {
    return method === 'card'
      ? 'credit-card'
      : method === 'cash'
        ? 'cash-stack'
        : 'wallet2';
  }

  // Representative method for the row icon: the non-credit tender if there is
  // one (cash/card), else the first tender's method.
  private _primaryMethod(sale: ISale): string {
    const tenders = sale.tenders ?? [];
    return (
      (tenders.find((t) => t.method !== 'credit') ?? tenders[0])?.method ?? ''
    );
  }

  private _renderSale(sale: ISale) {
    const refundState = this._refundState(sale);
    const refunds = this._refundsFor(sale);
    const createdAt = new Date(sale.createdUtc);
    const canRefund = this._canRefund(sale);
    return html`
      <div
        class="sale-card tappable ${refundState === 'full' ? 'is-refunded' : ''}"
        role="button"
        tabindex="0"
        @click=${() => this._openDetail(sale)}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._openDetail(sale);
          }
        }}
      >
        <div class="sale-row">
          <span class="icon-tile">
            <sl-icon
              name=${refundState !== 'none' ? 'arrow-counterclockwise' : this._payIcon(this._primaryMethod(sale))}
            ></sl-icon>
          </span>
          <div class="body">
            <div class="meta">
              ${
                sale.daySequence > 0
                  ? html`<span class="seq">#${sale.daySequence}</span>`
                  : nothing
              }
              <span>${createdAt.toLocaleTimeString()}</span>
              <sl-badge variant="neutral" pill>${sale.displayMethod}</sl-badge>
              ${
                sale.memberDisplayName
                  ? html`<sl-badge variant="success" pill
                      >${sale.memberDisplayName}</sl-badge
                    >`
                  : nothing
              }
              ${
                refundState === 'full'
                  ? html`<sl-badge variant="danger" pill>Refunded</sl-badge>`
                  : refundState === 'partial'
                    ? html`<sl-badge variant="warning" pill
                        >Partially refunded</sl-badge
                      >`
                    : nothing
              }
              ${
                sale.status === 'cancelled'
                  ? html`<sl-badge variant="neutral" pill>Cancelled</sl-badge>`
                  : sale.status !== 'completed'
                    ? html`<sl-badge variant="primary" pill
                        >${sale.status}</sl-badge
                      >`
                    : nothing
              }
            </div>
            <div class="sale-lines">
              ${sale.lines
                .map(
                  (l) =>
                    `${l.quantity}× ${l.nameSnapshot}${l.variantLabelSnapshot ? ` (${l.variantLabelSnapshot})` : ''}`
                )
                .join(' · ')}
            </div>
            ${
              refunds.length > 0
                ? html`<div class="refunds-row">
                    <div class="badge-row">
                      ${refunds.map(
                        (r) => html`
                          <sl-badge
                            pill
                            variant=${r.status === 'completed' ? 'warning' : 'neutral'}
                            >${r.refundMethod} refund —
                            ${r.amount.toFixed(2)}</sl-badge
                          >
                        `
                      )}
                    </div>
                  </div>`
                : nothing
            }
          </div>
          <div class="trail">
            <div class="total">${sale.total.toFixed(2)}</div>
            ${
              canRefund
                ? html`<button
                    class="btn-secondary-pill sm danger"
                    ?disabled=${this._busy}
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      this._openRefundDialog(sale);
                    }}
                  >
                    <sl-icon name="arrow-counterclockwise"></sl-icon>
                    Refund
                  </button>`
                : nothing
            }
          </div>
        </div>
      </div>
    `;
  }

  private _clearFilters = (): void => {
    this._search = '';
    this._timeBlock = '';
    this._payFilter = '';
  };

  private _toInputDate(s: string): string {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }

  render() {
    if (!this.activeShop) {
      return html`<p>No active shop.</p>`;
    }
    const filtered = this._filteredSales();
    return html`
      <div class="page-head">
        <h1 class="page-head__title">Sales history</h1>
        <p class="page-head__sub">
          Browse a day's sales. Tap a sale for its full detail and refunds; use
          <strong>Refund</strong> on a completed sale to return some or all of
          it. Active orders are cancelled from the active board, not here.
        </p>
      </div>

      ${this._renderToolbar()}
      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        this._loading
          ? html`<div class="empty">Loading…</div>`
          : this._sales.length === 0
            ? html`<div class="empty">No sales on ${this._dateLabel()}.</div>`
            : filtered.length === 0
              ? html`<div class="empty">
                  No sales match your filters.
                  <button
                    class="btn-secondary-pill sm"
                    @click=${this._clearFilters}
                  >
                    Clear filters
                  </button>
                </div>`
              : html`
                  <div class="result-count">
                    ${
                      this._hasActiveFilters()
                        ? html`${filtered.length} of ${this._sales.length}`
                        : html`${this._sales.length}`
                    }
                    sale${this._sales.length === 1 ? '' : 's'}
                  </div>
                  ${filtered.map((s) => this._renderSale(s))}
                `
      }
      ${this._renderDetailDialog()} ${this._renderRefundDialog()}
    `;
  }

  private _renderToolbar() {
    return html`
      <div class="toolbar">
        <div class="date-nav">
          <sl-icon-button
            name="chevron-left"
            label="Previous day"
            @click=${() => this._shiftDay(-1)}
          ></sl-icon-button>
          <input
            class="date-input"
            type="date"
            .value=${this._toInputDate(this._selectedDate)}
            max=${this._toInputDate(SalesHistoryPage._todayYyyyMmDd())}
            @change=${this._onDatePick}
          />
          <sl-icon-button
            name="chevron-right"
            label="Next day"
            ?disabled=${this._isToday()}
            @click=${() => this._shiftDay(1)}
          ></sl-icon-button>
          ${
            !this._isToday()
              ? html`<button
                  class="btn-secondary-pill sm"
                  @click=${() => this._goToDate(SalesHistoryPage._todayYyyyMmDd())}
                >
                  Today
                </button>`
              : nothing
          }
        </div>
        <div class="filters">
          <sl-input
            class="search"
            size="small"
            clearable
            placeholder="Search # / member / item"
            .value=${this._search}
            @sl-input=${(e: Event) => (this._search = (e.target as HTMLInputElement).value)}
          >
            <sl-icon slot="prefix" name="search"></sl-icon>
          </sl-input>
          <sl-select
            size="small"
            value=${this._timeBlock}
            @sl-change=${(e: Event) =>
              (this._timeBlock = (e.target as HTMLSelectElement).value)}
          >
            <sl-option value="">Any time</sl-option>
            <sl-option value="morning">Morning (5–11)</sl-option>
            <sl-option value="midday">Midday (11–14)</sl-option>
            <sl-option value="afternoon">Afternoon (14–17)</sl-option>
            <sl-option value="evening">Evening (17–22)</sl-option>
            <sl-option value="late">Late (22–5)</sl-option>
          </sl-select>
          <sl-select
            size="small"
            value=${this._payFilter}
            @sl-change=${(e: Event) =>
              (this._payFilter = (e.target as HTMLSelectElement).value)}
          >
            <sl-option value="">Any payment</sl-option>
            <sl-option value="cash">Cash</sl-option>
            <sl-option value="card">Card</sl-option>
            <sl-option value="other">Other</sl-option>
          </sl-select>
        </div>
      </div>
    `;
  }

  private _renderDetailDialog() {
    const sale = this._detailSale;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label=${sale ? `Sale #${sale.daySequence || '—'}` : 'Sale'}
        ?open=${this._detailOpen}
        @sl-request-close=${() => (this._detailOpen = false)}
      >
        ${sale ? this._renderDetailBody(sale) : nothing}
        <sl-button slot="footer" @click=${() => (this._detailOpen = false)}
          >Close</sl-button
        >
        ${
          sale && this._canRefund(sale)
            ? html`<sl-button
                slot="footer"
                variant="warning"
                @click=${() => this._refundFromDetail(sale)}
              >
                <sl-icon slot="prefix" name="arrow-counterclockwise"></sl-icon>
                Refund
              </sl-button>`
            : nothing
        }
      </sl-dialog>
    `;
  }

  private _renderDetailBody(sale: ISale) {
    const created = new Date(sale.createdUtc);
    const refunds = this._refundsFor(sale);
    const refundedTotal = this._completedRefunds(sale).reduce(
      (s, r) => s + r.amount,
      0
    );
    return html`
      <div class="detail">
        <div class="detail-head">
          <div class="meta">
            <span>${created.toLocaleString()}</span>
            <sl-badge variant="neutral" pill>${sale.displayMethod}</sl-badge>
            ${
              sale.status !== 'completed'
                ? html`<sl-badge
                    variant=${sale.status === 'cancelled' ? 'neutral' : 'primary'}
                    pill
                    >${sale.status}</sl-badge
                  >`
                : nothing
            }
          </div>
          ${
            sale.memberDisplayName
              ? html`<div class="detail-member">
                  <sl-icon name="person-badge"></sl-icon>
                  <span
                    >Member: <strong>${sale.memberDisplayName}</strong></span
                  >
                </div>`
              : html`<div class="detail-member muted">Walk-in (no member)</div>`
          }
          ${
            sale.actingStaffDisplayName
              ? html`<div class="detail-member">
                  <sl-icon name="person"></sl-icon>
                  <span
                    >Sold with <strong>${sale.actingStaffDisplayName}</strong>'s
                    PIN</span
                  >
                </div>`
              : nothing
          }
        </div>

        <table class="detail-lines">
          <tbody>
            ${sale.lines.map((l) => {
              const sels = (l.selectedModifierGroups ?? []).flatMap(
                (g) => g.selections
              );
              const named = sels.filter((s) => s.nameSnapshot);
              // Names when snapshotted; "+N options" only for legacy rows that
              // predate the modifier-name snapshot.
              const optLabel = named.length
                ? named
                    .map(
                      (s) =>
                        `${s.nameSnapshot}${s.quantity > 1 ? ` ×${s.quantity}` : ''}`
                    )
                    .join(', ')
                : sels.length
                  ? `+${sels.length} option${sels.length === 1 ? '' : 's'}`
                  : '';
              return html`<tr>
                <td class="dl-qty">${l.quantity}×</td>
                <td class="dl-name">
                  ${l.nameSnapshot}${
                    l.variantLabelSnapshot
                      ? html` <span class="dl-variant"
                          >${l.variantLabelSnapshot}</span
                        >`
                      : nothing
                  }
                  ${optLabel ? html`<div class="dl-mods">${optLabel}</div>` : nothing}
                </td>
                <td class="dl-amt">
                  ${(l.priceSnapshot * l.quantity).toFixed(2)}
                </td>
              </tr>`;
            })}
          </tbody>
        </table>

        <div class="detail-totals">
          <div>
            <span>Subtotal</span><span>${sale.subtotal.toFixed(2)}</span>
          </div>
          ${
            sale.lineDiscountTotal > 0
              ? html`<div>
                  <span>Line discounts</span
                  ><span>−${sale.lineDiscountTotal.toFixed(2)}</span>
                </div>`
              : nothing
          }
          ${
            sale.cartDiscountAmount > 0
              ? html`<div>
                  <span>Cart discount</span
                  ><span>−${sale.cartDiscountAmount.toFixed(2)}</span>
                </div>`
              : nothing
          }
          ${
            sale.tax > 0
              ? html`<div>
                  <span>Tax</span><span>${sale.tax.toFixed(2)}</span>
                </div>`
              : nothing
          }
          <div class="grand">
            <span>Total</span><span>${sale.total.toFixed(2)}</span>
          </div>
          ${
            refundedTotal > 0
              ? html`<div class="refunded">
                  <span>Refunded</span><span>−${refundedTotal.toFixed(2)}</span>
                </div>`
              : nothing
          }
        </div>

        ${
          refunds.length > 0
            ? html`<div class="detail-refunds">
                <h4>Refunds</h4>
                ${refunds.map(
                  (r) =>
                    html`<div class="rfd-row">
                      <sl-badge
                        pill
                        variant=${r.status === 'completed' ? 'warning' : 'neutral'}
                        >${r.refundMethod}</sl-badge
                      >
                      <span class="rfd-amt">${r.amount.toFixed(2)}</span>
                      <span class="muted">${r.status}</span>
                      ${r.reason ? html`<span class="muted">— ${r.reason}</span>` : nothing}
                    </div>`
                )}
              </div>`
            : nothing
        }
      </div>
    `;
  }

  private _renderRefundDialog() {
    const sale = this._refundSale;
    const remainingMoney = sale ? this._remainingMoney(sale) : 0;
    const selectedSubtotal = sale ? this._selectedSubtotal(sale) : 0;
    const valid = sale ? this._refundValid(sale) : false;
    const apology = this._refundAmount > selectedSubtotal + 0.005;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Refund sale"
        ?open=${this._refundDialogOpen}
        @sl-request-close=${() => (this._refundDialogOpen = false)}
      >
        ${
          sale
            ? html`
                <p class="meta">
                  Sale #${sale.daySequence || '—'} —
                  <strong>${remainingMoney.toFixed(2)}</strong> still
                  refundable. Tick the items that were a problem.
                </p>
                <div class="refund-lines">
                  ${sale.lines.map((l, i) => this._renderRefundLine(sale, l, i))}
                </div>

                <div class="refund-amount">
                  <sl-input
                    type="number"
                    label="Amount to return"
                    min="0"
                    max=${remainingMoney}
                    step="0.01"
                    .value=${String(this._refundAmount || '')}
                    @sl-input=${(e: Event) => {
                      this._refundAmountTouched = true;
                      const v = parseFloat(
                        (e.target as HTMLInputElement).value
                      );
                      this._refundAmount = Number.isFinite(v) ? v : 0;
                    }}
                  ></sl-input>
                  <small class="amount-hint">
                    Picked items:
                    <strong>${selectedSubtotal.toFixed(2)}</strong>.
                    ${
                      apology
                        ? html`Returning
                            <strong>${this._refundAmount.toFixed(2)}</strong>
                            (extra is goodwill).`
                        : html`Adjustable up to ${remainingMoney.toFixed(2)}.`
                    }
                  </small>
                </div>

                <sl-radio-group
                  label="Refund method"
                  .value=${this._refundMethod}
                  @sl-change=${(e: Event) =>
                    (this._refundMethod = (e.target as HTMLInputElement)
                      .value as RefundMethod)}
                >
                  <sl-radio value="cash"
                    >Cash (returns money from the till)</sl-radio
                  >
                  <sl-radio value="card"
                    >Card (process on the card terminal)</sl-radio
                  >
                  <sl-radio value="other"
                    >Other (voucher — tracked manually)</sl-radio
                  >
                </sl-radio-group>
                <sl-textarea
                  label="Reason (optional)"
                  rows="2"
                  .value=${this._refundReason}
                  @sl-input=${(e: Event) =>
                    (this._refundReason = (
                      e.target as HTMLTextAreaElement
                    ).value)}
                ></sl-textarea>
              `
            : nothing
        }
        <sl-button
          slot="footer"
          @click=${() => (this._refundDialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="warning"
          ?loading=${this._busy}
          ?disabled=${this._busy || !valid}
          @click=${this._submitRefund}
          >Refund
          ${this._refundAmount > 0 ? this._refundAmount.toFixed(2) : ''}</sl-button
        >
      </sl-dialog>
    `;
  }

  private _renderRefundLine(
    sale: ISale,
    line: ISale['lines'][number],
    i: number
  ) {
    const remaining = this._remainingQty(sale, i);
    const picked = this._refundQty.get(i) ?? 0;
    const name = `${line.nameSnapshot}${line.variantLabelSnapshot ? ` (${line.variantLabelSnapshot})` : ''}`;
    if (remaining === 0) {
      return html`<div class="refund-line is-done">
        <div class="rl-name">${name}</div>
        <div class="rl-note">fully refunded</div>
      </div>`;
    }
    return html`<div class="refund-line ${picked > 0 ? 'is-picked' : ''}">
      <div class="rl-name">
        ${name}
        <small
          >${line.priceSnapshot.toFixed(2)} each · ${remaining}
          refundable</small
        >
      </div>
      <div class="rl-stepper">
        <sl-icon-button
          name="dash-circle"
          label="One fewer"
          ?disabled=${picked <= 0}
          @click=${() => this._setLineQty(sale, i, picked - 1)}
        ></sl-icon-button>
        <span class="rl-qty">${picked}</span>
        <sl-icon-button
          name="plus-circle"
          label="One more"
          ?disabled=${picked >= remaining}
          @click=${() => this._setLineQty(sale, i, picked + 1)}
        ></sl-icon-button>
      </div>
    </div>`;
  }
}
